/**
 * useFocusTrap Hook
 * Traps keyboard focus within a container element for accessibility
 * Essential for modal dialogs, dropdowns, and overlay components
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * Focusable element selector
 * Covers all standard focusable elements
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Options for focus trap behavior
 */
interface FocusTrapOptions {
  /** Whether the focus trap is active */
  isActive: boolean;
  /** Whether to auto-focus the first focusable element on activation */
  autoFocus?: boolean;
  /** Whether to restore focus to the previously focused element on deactivation */
  restoreFocus?: boolean;
  /** Callback when escape key is pressed */
  onEscape?: () => void;
}

/**
 * Hook to trap keyboard focus within a container element
 * Ensures accessible modal and overlay behavior
 *
 * @param options - Focus trap configuration
 * @returns Ref to attach to the container element
 *
 * @example
 * const containerRef = useFocusTrap({
 *   isActive: isModalOpen,
 *   autoFocus: true,
 *   restoreFocus: true,
 *   onEscape: closeModal,
 * });
 *
 * <div ref={containerRef}>
 *   <button>First focusable</button>
 *   <button>Last focusable</button>
 * </div>
 */
export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(
  options: FocusTrapOptions
): React.RefObject<T | null> {
  const {
    isActive,
    autoFocus = true,
    restoreFocus = true,
    onEscape,
  } = options;

  const containerRef = useRef<T>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  /**
   * Get all focusable elements within the container
   */
  const getFocusableElements = useCallback((): HTMLElement[] => {
    if (!containerRef.current) return [];
    const elements = containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    return Array.from(elements).filter((el) => {
      // Additional checks for visibility
      return el.offsetParent !== null && !el.hasAttribute('hidden');
    });
  }, []);

  /**
   * Handle Tab key to cycle focus within container
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      if (!isActive || !containerRef.current) return;

      // Handle Escape key
      if (event.key === 'Escape' && onEscape) {
        event.preventDefault();
        event.stopPropagation();
        onEscape();
        return;
      }

      // Only trap Tab key
      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement;

      // Shift+Tab from first element -> go to last
      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      // Tab from last element -> go to first
      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
        return;
      }

      // If focus is outside the container, bring it back
      if (!containerRef.current.contains(activeElement)) {
        event.preventDefault();
        firstElement.focus();
      }
    },
    [isActive, getFocusableElements, onEscape]
  );

  /**
   * Handle focus events to prevent focus from leaving container
   */
  const handleFocusIn = useCallback(
    (event: FocusEvent): void => {
      if (!isActive || !containerRef.current) return;

      const target = event.target as HTMLElement;

      // If focus moved outside the container, bring it back
      if (!containerRef.current.contains(target)) {
        const focusableElements = getFocusableElements();
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    },
    [isActive, getFocusableElements]
  );

  // Set up and tear down focus trap
  useEffect(() => {
    if (!isActive) return;

    // Store previously focused element
    if (restoreFocus) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement;
    }

    // Auto-focus first element
    if (autoFocus) {
      // Small delay to ensure the container is rendered
      const timeoutId = setTimeout(() => {
        const focusableElements = getFocusableElements();
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        } else if (containerRef.current) {
          // If no focusable elements, focus the container itself
          containerRef.current.tabIndex = -1;
          containerRef.current.focus();
        }
      }, 10);

      return () => clearTimeout(timeoutId);
    }
  }, [isActive, autoFocus, getFocusableElements, restoreFocus]);

  // Add event listeners
  useEffect(() => {
    if (!isActive) return;

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);

      // Restore focus on cleanup
      if (restoreFocus && previouslyFocusedRef.current) {
        previouslyFocusedRef.current.focus();
        previouslyFocusedRef.current = null;
      }
    };
  }, [isActive, handleKeyDown, handleFocusIn, restoreFocus]);

  return containerRef;
}

export default useFocusTrap;
